import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { UserPermissionsService } from '../../user-permissions.service';
import { TargetService } from 'src/app/sidenav/target.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  of,
  startWith,
  Subscription,
} from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { CoreTargetService } from 'src/app/core/services/target.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { whiteLabelTarget } from 'src/app/utils/whiteLabel';

@Component({
  selector: 'app-organic-payment-config',
  templateUrl: './organic-payment-config.component.html',
  styleUrls: [
    '../user-action/user-action.component.css',
    './organic-payment-config.component.css',
  ],
})
export class OrganicPaymentConfigComponent {
  constructor(
    public dialogRef: MatDialogRef<OrganicPaymentConfigComponent>,
    private fb: FormBuilder,
    public targetService: TargetService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private translate: TranslateService,
    private service: UserPermissionsService,
    private coreTargetService: CoreTargetService,
    private localStorageService: LocalStorageService,
    private notificationService: NotificationService,
  ) {}

  userActionsSub: Subscription = new Subscription();

  public form: FormGroup = this.fb.group({
    billingInterval: [this.data?.billingInterval, Validators.required],
    organicKeywordsNum: [this.data?.organicKeywordsNum, Validators.required],
    currency: [this.data?.currency || 'USD', Validators.required],
    price: [this.data?.priceAmount, Validators.required],
    stripeAccount: [this.data?.stripeAccount || '', Validators.required],
    displayPackages: [this.data?.displayPackages || false],
    package1Keywords: [
      this.data?.packages?.find((item: any) => item.name === 'package2')
        ?.keywords,
    ],
    package1Price: [
      this.data?.packages?.find((item: any) => item.name === 'package2')?.price,
    ],
    package2Keywords: [
      this.data?.packages?.find((item: any) => item.name === 'package3')
        ?.keywords,
    ],
    package2Price: [
      this.data?.packages?.find((item: any) => item.name === 'package3')?.price,
    ],
  });

  isNew: any;
  oldData: any;
  formValueChanged = false;

  billingIntervalList: any = [
    { value: 'month', viewValue: 'Monthly' },
    { value: 'year', viewValue: 'Yearly' },
  ];

  userTargets: any = this.localStorageService.get('shopId_list') || [];

  isProcessing: boolean = false;

  selectedStore: string = '';
  selectedTargets: string[] = [];

  isClearPressed: boolean = false;

  currencyList: any = [];
  selectedCurrency: any = '';

  banksList: any = [];
  selectedBank: any = '';

  whiteLabelTarget = whiteLabelTarget;

  ngOnInit() {
    if (this.localStorageService.get('shopId_list')) {
      this.userTargets = this.localStorageService.get('shopId_list');
    }

    // if (this.data.target) {
    //   this.selectedTargets = this.data.target.split(',');
    // }

    this.selectedStore =
      this.coreTargetService.getSelectedTarget('target') || '';

    this.getCurrencyList();

    const displayCtrl = this.form.get('displayPackages');
    const priceCtrl = this.form.get('price');
    const organicCtrl = this.form.get('organicKeywordsNum');

    // Enable/Disable displayPackages
    this.userActionsSub.add(
      combineLatest([
        priceCtrl!.valueChanges.pipe(startWith(priceCtrl!.value)),
        organicCtrl!.valueChanges.pipe(startWith(organicCtrl!.value)),
      ])
        .pipe(
          map(([price, org]) => Number(price) > 0 && Number(org) > 0),
          distinctUntilChanged(),
        )
        .subscribe((shouldEnable) => {
          if (shouldEnable && displayCtrl!.disabled) {
            displayCtrl!.enable({ emitEvent: false });
          } else if (!shouldEnable && displayCtrl!.enabled) {
            displayCtrl!.disable({ emitEvent: false });
          }
        }),
    );

    this.isNew = true;
    if (this.data) {
      this.isNew = false;

      this.oldData = this.form.value;
      this.userActionsSub.add(
        this.form.valueChanges.subscribe((data) => {
          if (
            data?.billingInterval ||
            data?.organicKeywordsNum ||
            data?.price ||
            data?.currency ||
            data?.stripeAccount
          ) {
            this.isClearPressed = false;
          }
          if (
            JSON.stringify(this.oldData) === JSON.stringify(this.form.value)
          ) {
            this.formValueChanged = false;
          } else {
            this.formValueChanged = true;
          }

          this.selectedCurrency = this.currencyList.find(
            (item: any) => item.code === this.form.value.currency,
          )?.symbol;

          this.selectedBank = this.banksList.find(
            (item: any) => item.stripeAccount === this.form.value.stripeAccount,
          )?.name;
        }),
      );
    } else {
      this.userActionsSub.add(
        this.form.valueChanges.subscribe((data) => {
          this.selectedCurrency = this.currencyList.find(
            (item: any) => item.code === this.form.value.currency,
          )?.symbol;
        }),
      );
    }
  }
  ngOnDestroy(): void {
    this.userActionsSub?.unsubscribe();
  }
  clearForm() {
    this.isClearPressed = true;
    this.form.reset();
  }
  getCurrencyList() {
    this.userActionsSub.add(
      this.service.getCurrencies().subscribe((data) => {
        this.currencyList = data.currencies;

        this.banksList = data.banks;

        this.selectedCurrency = this.currencyList.find(
          (item: any) => item.code === this.form.value.currency,
        )?.symbol;

        this.selectedBank = this.banksList.find(
          (item: any) => item.stripeAccount === this.form.value.stripeAccount,
        )?.name;

        if (this.whiteLabelTarget) {
          this.form.get('stripeAccount')?.setValue('behamics_inc');
          this.form.get('stripeAccount')?.disable();
        }
      }),
    );
  }
  editConfig() {
    const paramObj = {
      billingInterval: this.form.getRawValue()?.['billingInterval'],
      organicKeywordsNum: this.form.getRawValue()?.['organicKeywordsNum'],
      currency: this.form.getRawValue()?.['currency'],
      price: this.form.getRawValue()?.['price'],
      stripeAccount: this.form.getRawValue()?.['stripeAccount'],
      forOrganic: true,
      target: this.selectedStore,
      ...(this.isClearPressed ? { deleted: true } : {}),

      // custom packages
      displayPackages: this.form.getRawValue()?.['displayPackages'],
      package2Price: this.form.getRawValue()?.['package1Price'],
      package2Keywords: this.form.getRawValue()?.['package1Keywords'],
      package3Price: this.form.getRawValue()?.['package2Price'],
      package3Keywords: this.form.getRawValue()?.['package2Keywords'],
    };

    this.isProcessing = true;
    this.userActionsSub.add(
      this.service.organicPaymentConfig(paramObj).subscribe({
        next: (data) => {
          this.isProcessing = false;
          this.dialogRef.close('updated');
        },
        error: (error) => {
          this.isProcessing = false;
          this.notificationService.error(error?.error?.error);
        },
      }),
    );
  }
  getTranslatedText(text: string, type: string = 'words'): Observable<string> {
    return this.translate.get(`${type}.${text}`).pipe(
      map((translated: string) =>
        translated === `${type}.${text}` ? text : translated,
      ),
      catchError(() => of(text)),
    );
  }
  isFormValid(isEdit: boolean = false) {
    if (this.isClearPressed) {
      return true;
    }

    if (
      (isEdit && this.form.pristine) ||
      !this.form.valid ||
      this.isProcessing
    ) {
      return false;
    }
    return true;
  }
  public getAdditionalPackages(event: any) {
    if (
      !this.form.get('organicKeywordsNum')?.value ||
      !this.form.get('price')?.value ||
      !event.checked ||
      this.form.getRawValue()?.['package1Price'] ||
      this.form.getRawValue()?.['package1Keywords'] ||
      this.form.getRawValue()?.['package2Price'] ||
      this.form.getRawValue()?.['package2Keywords']
    ) {
      return;
    }

    const params = {
      target: this.selectedStore,
      organicKeywordsNum: this.form.getRawValue()?.['organicKeywordsNum'],
      price: this.form.getRawValue()?.['price'],
    };

    this.userActionsSub.add(
      this.service.getExtraPackages(params).subscribe((data) => {
        this.form
          .get('package1Keywords')
          ?.setValue(data?.package2?.keywords || 0);
        this.form.get('package1Price')?.setValue(data?.package2?.price || 0);
        this.form
          .get('package2Keywords')
          ?.setValue(data?.package3?.keywords || 0);
        this.form.get('package2Price')?.setValue(data?.package3?.price || 0);
      }),
    );
  }
}
